import { IdsList } from '@shared/types/dev';

/**
 * Extracts elements from a query parameter that can be either a comma-separated string or an array.
 *
 * - If `param` is a string, it is split on comma (`,`) and the resulting string array is returned.
 * - If `param` is already an array, it is returned unchanged.
 * - If `param` is neither a string nor an array, the function returns `undefined`.
 *
 * Note: splitting does not trim values; behaviour mirrors `String.prototype.split`.
 *
 * @template T
 * @param {IdsList | T[]} param - The query parameter to extract elements from.
 * @returns {T[] | string[] | undefined} An array of elements when `param` is a string or array, otherwise `undefined`.
 *
 * @example
 * extractElementsFromQueryParam('a,b,c') // => ['a','b','c']
 * extractElementsFromQueryParam(['a','b']) // => ['a','b']
 */
export function extractElementsFromQueryParam<T>(param: IdsList | T[]): T[] | string[] {
  if (!param) return [] as T[];

  if (typeof param === 'string') {
    return param.split(',');
  } else if (Array.isArray(param)) {
    return param.map((item) => (typeof item === 'string' ? item : String(item)));
  }
}
